/**
 * Consolidated per-category breakdown. One row per `DealCategory` in the
 * canonical display order, carrying the headline match% and exclusion points;
 * each row expands inline to reveal the per-question detail (preference,
 * points, and any exclusion reason) that drove those numbers.
 *
 * Replaces the old split between `CategoryTable` (headline numbers) and
 * `ContributionsAccordion` (per-question drill-in) — the two were redundant,
 * showing the same categories twice. This single table lives in the right
 * column beside the spider chart.
 */
import { Fragment, useState } from 'react';
import { Icon, Label, Table } from 'semantic-ui-react';
import {
  CategoryBreakdown,
  CATEGORY_DISPLAY_ORDER,
  CATEGORY_LABELS,
  DealCategory,
  MatchContribution,
  MatchExclusion,
} from '../types';

interface Props {
  categories: CategoryBreakdown[];
  contributions: MatchContribution[];
  exclusions: MatchExclusion[];
  /** Question keys with no applicable deal data or no buyer answer. */
  skipped: string[];
  labelFor: (questionKey: string) => string;
  /** Maps a question key to its scoring category, for placing skipped rows. */
  categoryFor: (questionKey: string) => DealCategory | undefined;
}

const KILL_THRESHOLD = 5;

const PREF_COLORS: Record<MatchContribution['preference'], 'green' | 'yellow' | 'red' | 'grey'> = {
  yes: 'green',
  maybe: 'yellow',
  no: 'red',
  missing: 'grey',
};

const PREF_LABELS: Record<MatchContribution['preference'], string> = {
  yes: 'yes',
  maybe: 'maybe',
  no: 'no',
  missing: 'no answer',
};

export default function CategoryBreakdownTable({
  categories,
  contributions,
  exclusions,
  skipped,
  labelFor,
  categoryFor,
}: Props) {
  const [expanded, setExpanded] = useState<Set<DealCategory>>(new Set());

  const toggle = (category: DealCategory) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });

  const byCategory = new Map<DealCategory, CategoryBreakdown>();
  for (const c of categories) byCategory.set(c.category, c);

  const contributionsByCategory = new Map<DealCategory, MatchContribution[]>();
  for (const c of contributions) {
    const list = contributionsByCategory.get(c.category) ?? [];
    list.push(c);
    contributionsByCategory.set(c.category, list);
  }

  const exclusionByQuestion = new Map<string, MatchExclusion>();
  for (const e of exclusions) exclusionByQuestion.set(e.questionKey, e);

  const skippedByCategory = new Map<DealCategory, string[]>();
  for (const key of skipped) {
    const category = categoryFor(key);
    if (!category) continue;
    const list = skippedByCategory.get(category) ?? [];
    list.push(key);
    skippedByCategory.set(category, list);
  }

  return (
    <Table compact basic="very" fixed className="buyer-profile-match__category-table">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell width={8}>Category</Table.HeaderCell>
          <Table.HeaderCell width={4} textAlign="right">
            Match
          </Table.HeaderCell>
          <Table.HeaderCell width={4} textAlign="right">
            Exclusion
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {CATEGORY_DISPLAY_ORDER.map((category) => {
          const breakdown = byCategory.get(category);
          const rows = contributionsByCategory.get(category) ?? [];
          const skippedKeys = skippedByCategory.get(category) ?? [];
          const empty = !breakdown || breakdown.possible === 0;
          const open = expanded.has(category);
          const killed = !!breakdown && breakdown.exclusion >= KILL_THRESHOLD;
          const killClass = killed ? 'buyer-profile-match__row--killed' : '';
          const expandable = rows.length > 0 || skippedKeys.length > 0;

          return (
            <Fragment key={category}>
              <Table.Row
                className={`${killClass} ${
                  expandable ? 'buyer-profile-match__cat-row--clickable' : ''
                }`.trim()}
                onClick={expandable ? () => toggle(category) : undefined}
              >
                <Table.Cell>
                  {expandable ? (
                    <Icon name={open ? 'dropdown' : 'caret right'} />
                  ) : (
                    <Icon name="caret right" style={{ visibility: 'hidden' }} />
                  )}
                  {CATEGORY_LABELS[category]}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {empty ? (
                    <em className="buyer-profile-match__muted">
                      Buyer profile incomplete
                    </em>
                  ) : (
                    `${breakdown!.percent}%`
                  )}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {empty ? (
                    ''
                  ) : breakdown!.exclusion > 0 ? (
                    <>
                      <Icon name="warning sign" color="yellow" />
                      {breakdown!.exclusion} pt
                      {breakdown!.exclusion === 1 ? '' : 's'}
                    </>
                  ) : (
                    '0 pts'
                  )}
                </Table.Cell>
              </Table.Row>

              {open && expandable ? (
                <Table.Row className="buyer-profile-match__cat-detail">
                  <Table.Cell colSpan={3}>
                    <div className="buyer-profile-match__cat-detail-inner">
                    <Table compact basic="very" fixed>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell width={8}>Question</Table.HeaderCell>
                          <Table.HeaderCell width={4} textAlign="right">
                            Preference
                          </Table.HeaderCell>
                          <Table.HeaderCell width={4} textAlign="right">
                            Points
                          </Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {rows.map((c) => {
                          const exclusion = exclusionByQuestion.get(c.questionKey);
                          return (
                            <Table.Row key={c.questionKey}>
                              <Table.Cell>
                                {labelFor(c.questionKey)}
                                {exclusion ? (
                                  <div className="buyer-profile-match__exclusion-reason">
                                    <Icon name="warning sign" color="yellow" />
                                    {exclusion.reason}
                                  </div>
                                ) : null}
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                <Label size="tiny" color={PREF_COLORS[c.preference]}>
                                  {PREF_LABELS[c.preference]}
                                </Label>
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                {c.points} / {c.possible}
                                {exclusion ? (
                                  <span className="buyer-profile-match__muted">
                                    {' '}
                                    · {exclusion.points} excl
                                  </span>
                                ) : null}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                        {skippedKeys.map((key) => (
                          <Table.Row
                            key={key}
                            className="buyer-profile-match__skipped-row"
                          >
                            <Table.Cell>{labelFor(key)}</Table.Cell>
                            <Table.Cell colSpan={2} textAlign="right">
                              <em className="buyer-profile-match__muted">
                                skipped
                              </em>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : null}
            </Fragment>
          );
        })}
      </Table.Body>
    </Table>
  );
}
